import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import { Event as EventIcon } from "@mui/icons-material";
import EventDetailsModal from "./EventDetailsModal";
import { PRIMARY_PINK } from "../constants";

function DayEventsModal({ open, onClose, events, selectedDate, onEventClick }) {
  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isUpcoming = (endTime) => {
    if (!endTime) return false;
    return new Date(endTime) > new Date();
  };

  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EventIcon sx={{ color: PRIMARY_PINK }} />
          <Typography variant="h5" component="div" fontWeight="bold">
            Events on {selectedDate ? formatDate(selectedDate) : "Selected Date"}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {events.length} event{events.length !== 1 ? 's' : ''} scheduled
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No events scheduled for this day.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {events.map((event, index) => {
              const isMajor = event.item_type === 'major' || (!event.start_time && !!event.start_date);
              return (
              <React.Fragment key={`${event.item_type || 'event'}-${event.id}`}>
                <ListItem 
                  sx={{ 
                    p: 2, 
                    mb: 1, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1976d2'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onClick={() => handleEventClick(event)}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                        {event.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                        {isMajor && (
                          <Chip label="Major" size="small" color="secondary" />
                        )}
                        {isUpcoming(event.end_time || event.end_date) && (
                          <Chip 
                            label="Upcoming" 
                            size="small"
                            sx={{ 
                              bgcolor: PRIMARY_PINK,
                              color: '#ffffff'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {isMajor
                        ? `${formatDate(event.start_date)} - ${formatDate(event.end_date)}`
                        : `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`}
                    </Typography>
                    
                    {event.location && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        📍 {event.location}
                      </Typography>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4,
                      whiteSpace: 'pre-line'
                    }}>
                      {event.description || event.content || "No description available."}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Hosted by {event.club_name || "Unknown Club"}
                      </Typography>
                      {(event.flyer_url || event.banner_url) && (
                        <Chip 
                          label={isMajor ? "Has Banner" : "Has Flyer"} 
                          size="small" 
                          variant="outlined"
                          sx={{
                            borderColor: PRIMARY_PINK,
                            color: PRIMARY_PINK
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </ListItem>
                {index < events.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            );
            })}
          </List>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            color: "#000000",
            borderColor: "#000000",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#000000",
              backgroundColor: "rgba(0,0,0,0.08)"
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DayEventsModal; 